import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import ONBOARDING_STEPS from './OnboardingSteps'

const TOTAL_STEPS = ONBOARDING_STEPS.length

export default function OnboardingTutorial() {
  const profile = useAuthStore((s) => s.profile)
  const completeOnboarding = useAuthStore((s) => s.completeOnboarding)
  const navigate = useNavigate()
  const location = useLocation()

  const [active, setActive] = useState(false)
  const [step, setStep] = useState(0)
  const [targetRect, setTargetRect] = useState(null)
  const [transitioning, setTransitioning] = useState(false)
  const [mounted, setMounted] = useState(false)
  const observerRef = useRef(null)
  const timeoutRef = useRef(null)
  const returnPathRef = useRef(null)
  // A replay from the FAQ should go back to the FAQ. A first run should not
  // go anywhere — see goNext. Both entry points set returnPathRef, so the
  // path alone cannot tell them apart.
  const isReplayRef = useRef(false)

  // Trigger logic
  useEffect(() => {
    // Trial users must get the tutorial too. This read profile.is_paid,
    // which is FALSE for a trial — the trial grants access through
    // subscription_expires_at instead, deliberately, so that it can lapse.
    // So the people who most need the walkthrough, on their first paid-for
    // look at the app, would have been the only ones not shown it.
    //
    // Same test ProtectedRoute uses to decide they may be here at all.
    const hasAccess = !!profile && (
      profile.is_lifetime
      || profile.is_paid
      || profile.subscription_status === 'active'
      || (profile.subscription_expires_at && new Date(profile.subscription_expires_at) > new Date())
    )
    if (profile && !profile.has_seen_onboarding && hasAccess) {
      // Remember where the user was so we can return them after the tutorial
      returnPathRef.current = location.pathname
      isReplayRef.current = false
      setActive(true)
      // Slight delay for mount animation
      requestAnimationFrame(() => setMounted(true))
    }
  }, [profile])

  // Allow re-triggering tutorial via custom event (e.g. from FAQ page)
  useEffect(() => {
    function handleReplay() {
      returnPathRef.current = location.pathname
      isReplayRef.current = true
      setStep(0)
      setActive(true)
      requestAnimationFrame(() => setMounted(true))
    }
    window.addEventListener('replay-tutorial', handleReplay)
    return () => window.removeEventListener('replay-tutorial', handleReplay)
  }, [location.pathname])

  const currentStep = ONBOARDING_STEPS[step]

  // Measure target element
  const scrolledRef = useRef(false)
  const measureTarget = useCallback(() => {
    if (!currentStep?.targetSelector) {
      setTargetRect(null)
      return true
    }
    // Use querySelectorAll and find the first visible element (non-zero dimensions)
    // This handles elements that exist in both mobile and desktop layouts
    const candidates = document.querySelectorAll(currentStep.targetSelector)
    let el = null
    for (const candidate of candidates) {
      const r = candidate.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) { el = candidate; break }
    }
    if (!el && candidates.length > 0) el = candidates[0] // fallback to first match
    if (el) {
      // Scroll target into view on first discovery
      if (!scrolledRef.current) {
        scrolledRef.current = true
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      const rect = el.getBoundingClientRect()
      const padding = currentStep.padding ?? 8
      setTargetRect({
        x: rect.left - padding,
        y: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        rx: currentStep.borderRadius ?? 16,
      })
      return true
    }
    return false
  }, [currentStep])

  // Navigate to step's page and find target
  useEffect(() => {
    if (!active) return

    // Clear stale target rect from previous step immediately
    setTargetRect(null)
    scrolledRef.current = false

    // Clean up previous observers/timeouts
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null }

    const stepData = ONBOARDING_STEPS[step]

    // Navigate if needed (compare pathname only, ignore query params for matching)
    if (stepData.page) {
      const targetPath = stepData.page.split('?')[0] || '/'
      if (location.pathname !== targetPath) {
        navigate(stepData.page)
      } else if (stepData.page.includes('?')) {
        // Same path but query params changed — re-navigate so the page picks them up.
        navigate(stepData.page, { replace: true })
      }
    }

    // Fullscreen step — no target needed
    if (!stepData.targetSelector) {
      setTargetRect(null)
      return
    }

    // Try measuring immediately
    if (measureTarget()) return

    // Set up MutationObserver to watch for the element
    const observer = new MutationObserver(() => {
      if (measureTarget()) {
        observer.disconnect()
        observerRef.current = null
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null }
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    observerRef.current = observer

    // 3-second timeout → fallback to fullscreen
    timeoutRef.current = setTimeout(() => {
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null }
      setTargetRect(null) // fullscreen fallback
    }, 3000)

    return () => {
      if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null }
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null }
    }
  }, [active, step, location.pathname, navigate, measureTarget])

  // Continuously re-measure target position (handles layout shifts from async content)
  useEffect(() => {
    if (!active || !currentStep?.targetSelector) return
    let rafId
    const tick = () => {
      measureTarget()
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [active, currentStep, measureTarget])

  const goNext = useCallback(() => {
    if (step >= TOTAL_STEPS - 1) {
      // Check for a saved return path from the payment/invite flow
      const savedReturn = localStorage.getItem('onboardingReturnPath')
      if (savedReturn) localStorage.removeItem('onboardingReturnPath')
      // A FIRST RUN ends wherever its last step left them, which is now the
      // settings page — and that is the point. The tour closes by asking
      // them to upload a picture and pick a backdrop, and of the 88 accounts
      // that signed up and went quiet, not one had done either. Dropping
      // them on /picks the instant they are told to do it put a navigation
      // between the instruction and the thing.
      //
      // Still navigates in the two cases that need it: a payment or invite
      // flow waiting to be resumed, and a replay from the FAQ, which should
      // give the FAQ back.
      const returnTo = savedReturn || (isReplayRef.current ? returnPathRef.current : null)
      setMounted(false)
      setTimeout(() => {
        setActive(false)
        completeOnboarding()
        if (returnTo) navigate(returnTo)
      }, 300)
      return
    }
    setTransitioning(true)
    setTimeout(() => {
      setStep((s) => s + 1)
      setTransitioning(false)
    }, 200)
  }, [step, completeOnboarding, navigate])

  const goBack = useCallback(() => {
    if (step <= 0) return
    setTransitioning(true)
    setTimeout(() => {
      setStep((s) => s - 1)
      setTransitioning(false)
    }, 200)
  }, [step])

  const handleSkip = useCallback(() => {
    const savedReturn = localStorage.getItem('onboardingReturnPath')
    if (savedReturn) localStorage.removeItem('onboardingReturnPath')
    setMounted(false)
    setTimeout(() => {
      setActive(false)
      completeOnboarding()
      if (savedReturn) navigate(savedReturn)
    }, 300)
  }, [completeOnboarding, navigate])

  if (!active) return null

  const isFullscreen = !targetRect
  const isFreeScroll = currentStep.freeScroll === true
  const progressPercent = ((step + 1) / TOTAL_STEPS) * 100

  // Tooltip position calculation
  const getTooltipStyle = () => {
    // Free scroll: card docked to right side on desktop, bottom on mobile
    if (isFreeScroll) {
      if (window.innerWidth < 768) {
        return {
          position: 'fixed',
          bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px) + 0.5rem)',
          left: '1rem',
          right: '1rem',
          maxWidth: '24rem',
          margin: '0 auto',
        }
      }
      return {
        position: 'fixed',
        top: '5rem',
        right: '1rem',
        width: '20rem',
        maxWidth: 'calc(100vw - 2rem)',
      }
    }

    if (isFullscreen) {
      // Center using inset + margin auto — no transform needed
      return {
        position: 'fixed',
        inset: 0,
        margin: 'auto',
        width: 'calc(100% - 2rem)',
        maxWidth: '24rem',
        height: 'fit-content',
      }
    }

    // Mobile: bottom-sheet style above BottomTabBar, EXCEPT when the
    // spotlight target sits near the top of the screen — in that case
    // the tooltip should anchor right under the target (otherwise the
    // arrow/instruction text is hundreds of px away from the thing it
    // describes, e.g. the home-logo step).
    if (window.innerWidth < 768) {
      const targetNearTop = targetRect.y < window.innerHeight * 0.33
      if (targetNearTop) {
        return {
          position: 'fixed',
          top: `${targetRect.y + targetRect.height + 12}px`,
          left: '1rem',
          right: '1rem',
          maxWidth: '24rem',
          margin: '0 auto',
        }
      }
      return {
        position: 'fixed',
        bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px) + 0.5rem)',
        left: '1rem',
        right: '1rem',
        maxWidth: '24rem',
        margin: '0 auto',
      }
    }

    // Desktop / tablet: position near the spotlight
    const preferredPos = currentStep.position || 'bottom'
    const style = {
      position: 'fixed',
      width: '22rem',
      maxWidth: 'calc(100vw - 2rem)',
    }

    // Estimate tooltip height (~220px) and check if it fits below
    const tooltipHeight = 220
    const spaceBelow = window.innerHeight - (targetRect.y + targetRect.height + 12)
    const spaceAbove = targetRect.y - 12

    // Auto-flip: if preferred is bottom but not enough space, go top (and vice versa)
    let pos = preferredPos
    if (pos === 'bottom' && spaceBelow < tooltipHeight && spaceAbove > spaceBelow) {
      pos = 'top'
    } else if (pos === 'top' && spaceAbove < tooltipHeight && spaceBelow > spaceAbove) {
      pos = 'bottom'
    }

    const leftPos = Math.max(16, Math.min(targetRect.x, window.innerWidth - 368))

    if (pos === 'top') {
      style.bottom = `${window.innerHeight - targetRect.y + 12}px`
      style.left = `${leftPos}px`
    } else {
      style.top = `${targetRect.y + targetRect.height + 12}px`
      style.left = `${leftPos}px`
    }

    return style
  }

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      style={{ pointerEvents: isFreeScroll ? 'none' : 'auto' }}
    >
      {/* Dark overlay — skip for freeScroll steps */}
      {!isFreeScroll && (
        isFullscreen ? (
          <div className="fixed inset-0 bg-black/75" />
        ) : (
          <svg
            className="fixed inset-0"
            width="100%"
            height="100%"
            style={{ display: 'block' }}
          >
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect
                  x={targetRect.x}
                  y={targetRect.y}
                  width={targetRect.width}
                  height={targetRect.height}
                  rx={targetRect.rx}
                  ry={targetRect.rx}
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.75)"
              mask="url(#spotlight-mask)"
            />
          </svg>
        )
      )}

      {/* Accent border ring around spotlight */}
      {targetRect && (
        <div
          className="fixed pointer-events-none"
          style={{
            left: targetRect.x - 1,
            top: targetRect.y - 1,
            width: targetRect.width + 2,
            height: targetRect.height + 2,
            borderRadius: targetRect.rx + 1,
            border: '2px solid rgba(255, 77, 0, 0.6)',
          }}
        />
      )}

      {/* Click blocker — skip for freeScroll steps */}
      {!isFreeScroll && <div className="fixed inset-0" />}

      {/* Tooltip card */}
      <div
        className={`tutorial-slide-up transition-all duration-200 ${
          transitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
        }`}
        style={{
          ...getTooltipStyle(),
          zIndex: 71,
          ...(isFreeScroll ? { pointerEvents: 'auto' } : {}),
        }}
      >
        {/* Glass edge: bg-bg-primary with a faint white border, the
            same treatment as member rows, league cards and the survivor
            board. Deliberately NOT bg-white/5 + backdrop-blur — the house
            style is a solid dark ground with a bright edge, not frosted
            glass. bg-bg-card read as a lighter grey box floating over the
            dimmed page; this sits on the same ground as everything else
            and is defined by its edge instead. */}
        <div className="bg-bg-primary border border-text-primary/20 rounded-2xl p-5 shadow-2xl overflow-hidden">
          {/* Header: Step counter + Skip */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-text-muted font-semibold">
              {step + 1} / {TOTAL_STEPS}
            </span>
            {step < TOTAL_STEPS - 1 && (
              <button
                onClick={handleSkip}
                className="text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                Skip tutorial
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div className="w-full h-1 bg-text-primary/10 rounded-full mb-4 overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Title */}
          <h2 className="font-display text-xl mb-2">{currentStep.title}</h2>

          {/* Body */}
          <p className="text-text-primary/90 text-sm mb-5 leading-relaxed">{currentStep.body}</p>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={goBack}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-text-secondary hover:text-text-primary hover:bg-text-primary/5 transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={goNext}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-accent text-white hover:bg-accent-hover transition-colors"
            >
              {currentStep.buttonText || 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
